import { DISCORD_INVITE_URL } from '@/lib/constants';
import { track } from '@/lib/analytics';

const steps = [
  {
    title: 'Create a Character',
    bullets: [
      'The group decides who creates and leads the party',
      'Each player signs in to Polymyth and creates a character',
      'The group shares the character names between them',
    ],
  },
  {
    title: 'Create a Party (Leader)',
    bullets: [
      'Create the party and invite the other characters to it',
      "Ask Tavian to invite each friend's character to the party",
    ],
  },
  {
    title: 'Join a Party (All Other Players)',
    bullets: [
      'Invites are visible in the Hilltop Camp',
      'Accept the invite from the party leader to join the party',
    ],
  },
  {
    title: 'Start the Adventure',
    bullets: [
      'The leader picks an adventure and starts it',
      'You now all adventure together as a party',
    ],
  },
];

function Step({ n, title, bullets }: { n: number; title: string; bullets: string[] }) {
  return (
    <div className="flex gap-4 border-b border-border/50 pb-6">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-600 text-white font-bold">
        {n}
      </div>
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
        <ul className="list-disc pl-5 space-y-1.5 text-lg font-medium text-foreground/70 leading-relaxed">
          {bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function MultiplayerGuidePage() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-20">
      <h1 className="text-4xl font-bold mb-3 text-foreground">Playing with Friends</h1>
      <p className="text-xl font-medium text-foreground/70 leading-relaxed mb-10">
        Bring your friends and play together. Form a party of real players, add NPC companions to
        fill out your ranks, and let the GM run the table for all of you.
      </p>

      <h2 className="text-2xl font-bold text-foreground mb-6">
        Follow the instructions below to play with your friends
      </h2>
      <div className="space-y-6">
        {steps.map((s, i) => (
          <Step key={s.title} n={i + 1} title={s.title} bullets={s.bullets} />
        ))}
      </div>

      <p className="text-lg font-medium text-foreground/70 leading-relaxed mt-12">
        Stuck on anything? Ask Tavian at the Hilltop Camp. He handles invitations, joining, leaving,
        and starting adventures right from the chat.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-14 mb-4">Learn more</h2>
      <p className="text-lg font-medium text-foreground/70 leading-relaxed">
        Want to talk multiplayer with other players, ask questions, or see what's coming next?{' '}
        <a
          href={DISCORD_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="multiplayer-guide-discord-cta"
          onClick={() => track('discord_feedback_cta_click', { surface: 'multiplayer_guide' })}
          className="text-primary underline underline-offset-4 hover:text-primary/80"
        >
          Join our Discord
        </a>{' '}
        to find other players to party up with, discuss multiplayer, and hear more about the
        feature.
      </p>
    </div>
  );
}
